/* eslint-disable @typescript-eslint/no-explicit-any */
import { getJwt, type Id, HttpError } from "./common";
import { deepTrim } from "../utils/deepTrim";

const BASE_URL = import.meta.env.VITE_API_URL;

// Query-string for the optional viewed-org scope used by users/roles (server authorizes it). Exported so
// those resources can layer org-aware list/create over the generic resource without changing its shape.
export const orgQuery = (organizationId?: string | null) =>
    organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : '';

// Org-scoped list/save for resources that follow the VIEWED org (users, roles). Kept separate from the
// generic createApiResource so its list/save keep their zero-arg shape (react-query queryFn inference).
export async function listWithOrg<T>(path: string, organizationId?: string | null): Promise<T[]> {
    const response = await fetch(`${BASE_URL}/${path}${orgQuery(organizationId)}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${getJwt()}` },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new HttpError(response.status, data);
    return data;
}

export async function saveWithOrg<T>(path: string, item: T, organizationId?: string | null): Promise<T | void> {
    const id = (item as any).id;   // eslint-disable-line @typescript-eslint/no-explicit-any
    // Create (no id) is scoped to the viewed org; update (has id) is scoped by the id itself.
    const url = id ? `${BASE_URL}/${path}/${id}` : `${BASE_URL}/${path}${orgQuery(organizationId)}`;
    const response = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        body: JSON.stringify(deepTrim(item)),
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getJwt()}` },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new HttpError(response.status, data);
    return id ? undefined : data;
}

export function createApiResource<TModel, TId extends Id = string>(path: string) {
    const URL = `${BASE_URL}/${path}`;

    return {
        list: async (): Promise<TModel[]> => {
            const response = await fetch(URL, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${getJwt()}`
                }
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
            return await data;
        },
        get: async (id: TId): Promise<TModel> => {
            const response = await fetch(`${URL}/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${getJwt()}`
                }
            });
            const data = await response.json();
            if (!response.ok) throw new HttpError(response.status, data);
            return await data;
        },
        save: async (item: TModel): Promise<TModel | void> => {
            // If the item has an id, we assume it's an update
            if ((item as any).id) {
                const response = await fetch(`${URL}/${(item as any).id}`, {
                    method: 'PUT',
                    body: JSON.stringify(deepTrim(item)),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getJwt()}`
                    },
                });

                const contentType = response.headers.get('content-type') ?? '';
                const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

                if (!response.ok) throw new HttpError(response.status, data);
            }
            else {
                const response = await fetch(URL, {
                    method: 'POST',
                    body: JSON.stringify(deepTrim(item)),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getJwt()}`
                    },
                });

                const contentType = response.headers.get('content-type') ?? '';
                const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

                if (!response.ok) throw new HttpError(response.status, data);
                return await data;
            }
        },
        create: async (item: TModel): Promise<TModel> => {
            const response = await fetch(URL, {
                method: 'POST',
                body: JSON.stringify(deepTrim(item)),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getJwt()}`
                },
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
            return await data;
        },
        update: async (item: TModel): Promise<void> => {
            const response = await fetch(`${URL}/${(item as any).id}`, {
                method: 'PUT',
                body: JSON.stringify(deepTrim(item)),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getJwt()}`
                },
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
        },
        delete: async (id: TId): Promise<void> => {
            const response = await fetch(`${URL}/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${getJwt()}`
                }
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
        }
    }
}
